import { LocaleProvider } from "./context/LocaleContext";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { StoreProvider } from "@/context/StoreContext";
import { Layout } from "@/components/layout/Layout";
import Home from "@/pages/Home";
import AnalyticsConsent from "@/components/AnalyticsConsent";
const StyleAdvisorPage = lazy(() => import("@/pages/StyleAdvisorPage"));
const Category = lazy(() => import("@/pages/Category"));
const Product = lazy(() => import("@/pages/Product"));
const Collection = lazy(() => import("@/pages/Collection"));
const GiftFinder = lazy(() => import("@/pages/GiftFinder"));
const Club = lazy(() => import("@/pages/Club"));
const Locations = lazy(() => import("@/pages/Locations"));
const Franchise = lazy(() => import("@/pages/Franchise"));
const Dealer = lazy(() => import("@/pages/Dealer"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Cart = lazy(() => import("@/pages/Cart"));
const Favorites = lazy(() => import("@/pages/Favorites"));
const SearchPage = lazy(() => import("@/pages/Search"));
const Auth = lazy(() => import("@/pages/Auth"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Admin = lazy(() => import("@/pages/Admin"));
const ErasureRequestQueue = lazy(() => import("@/components/ErasureRequestQueue"));
const Executive = lazy(() => import("@/pages/Executive"));
const Account = lazy(() => import("@/pages/Account"));
const PaymentResult = lazy(() => import("@/pages/PaymentResult"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const MobileApps = lazy(() => import("@/pages/MobileApps"));
const Blog = lazy(() => import("@/pages/Blog"));
const Legal = lazy(() => import("@/pages/Legal"));

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function useLenis() {
  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return undefined;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
}

function AppShell() {
  useLenis();
  return (
    <>
      <ScrollManager />
      <AnalyticsConsent />
      <Suspense fallback={<div className="min-h-[50vh]" aria-busy="true" />}><Routes>
        <Route path="/yonetim-merkezi" element={<Executive />} />
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/kategori/:slug" element={<Category />} />
          <Route path="/kategori/kolye" element={<Navigate to="/kategori/kolyeler" replace />} />
          <Route path="/kategori/kupe" element={<Navigate to="/kategori/kupeler" replace />} />
          <Route path="/kategori/yuzuk" element={<Navigate to="/kategori/yuzukler" replace />} />
          <Route path="/kategori/bileklik" element={<Navigate to="/kategori/bileklikler" replace />} />
          <Route path="/kategori/sac-aksesuar" element={<Navigate to="/kategori/sac-aksesuarlari" replace />} />
          <Route path="/urun/:slug" element={<Product />} />
          <Route path="/koleksiyon/:slug" element={<Collection />} />
          <Route path="/koleksiyon" element={<Navigate to="/kategori/tumu" replace />} />
          <Route path="/koleksiyonlar" element={<Navigate to="/kategori/tumu" replace />} />
          <Route path="/stil-danismani" element={<StyleAdvisorPage />} />
          <Route path="/hediye" element={<GiftFinder />} />
          <Route path="/shule-club" element={<Club />} />
          <Route path="/magazalar" element={<Locations type="store" />} />
          <Route path="/kiosklar" element={<Locations type="kiosk" />} />
          <Route path="/bayilik" element={<Franchise />} />
          <Route path="/kurumsal" element={<Dealer />} />
          <Route path="/mobil-uygulamalar" element={<MobileApps />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<Blog />} />
          <Route path="/hakkimizda" element={<About />} />
          <Route path="/iletisim" element={<Contact />} />
          {["/firma-bilgileri", "/tuketici-haklari", "/teslimat-ve-iade", "/guvenlik-politikasi", "/gizlilik", "/veri-silme", "/kullanim-kosullari"].map(path => <Route key={path} path={path} element={<Legal path={path} />} />)}
          <Route path="/sepet" element={<Cart />} />
          <Route path="/favoriler" element={<Favorites />} />
          <Route path="/ara" element={<SearchPage />} />
          <Route path="/giris" element={<Auth />} />
          <Route path="/sifre-sifirla" element={<ResetPassword />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/silme-talepleri" element={<ErasureRequestQueue />} />
          <Route path="/hesap" element={<Account />} />
          <Route path="/odeme/basarili" element={<PaymentResult type="success" />} />
          <Route path="/odeme/iptal" element={<PaymentResult type="cancel" />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes></Suspense>
    </>
  );
}

function App() {
  return (
    <LocaleProvider><HelmetProvider>
      <AuthProvider>
        <StoreProvider>
          <BrowserRouter>
            <AppShell />
            <Toaster position="top-center" toastOptions={{
              style: { background: "#FDFBF7", border: "1px solid #D8C7B5", color: "#2C221E" },
            }} />
          </BrowserRouter>
        </StoreProvider>
      </AuthProvider>
    </HelmetProvider></LocaleProvider>
  );
}

export default App;

